// src/screens/admin/CotizadorAjustes.jsx
// Ajustes fijos, horarios del ómnibus y el conversor de dólares del Cotizador.
import React, { useState } from 'react';
import { toast } from '../../components/UI';
import { guardarAjustes, guardarHorario, borrarHorario } from '../../services/cotizadorService';
import { TASAS_CONVERSOR, fmtBRL, esNum } from '../../services/cotizadorCalc';
import { Campo, NumInput, inputStyle, cardStyle, btnChico, labelStyle, useAutoGuardado, IndicadorGuardado } from './CotizadorUI';

// ── Ajustes ──────────────────────────────────────────────────────────
const CAMPOS = [
  { k: 'hostal_dia', label: 'Hostal por día (R$)' },
  { k: 'cargo_mediodia', label: 'Cargo si el ómnibus sale después de las 12:00 (R$)' },
  { k: 'comision_brt', label: 'Comisión BRT sobre la Tarifa (%)', escala: 100 },
  { k: 'tarifa_pct', label: 'Tarifa como % del Total BRT', escala: 100 },
  { k: 'factor_comprarviagem', label: 'Factor Comprarviagem' },
  { k: 'ganancia_minima', label: 'Ganancia mínima (R$)' },
  { k: 'omnibus_precio', label: 'Precio fijo ómnibus Boa Vista → Manaus (Total BRT)' },
  { k: 'margen_manaus_horas', label: 'Margen de seguridad en Manaus (horas)' },
  { k: 'margen_conexion_horas', label: 'Margen entre vuelo / ómnibus extra (horas)' },
  { k: 'milheiro_defecto', label: 'Milheiro por defecto (R$)' },
  { k: 'taxa_defecto', label: 'Taxa de embarque por defecto (R$)' },
  { k: 'usd_defecto', label: 'US$ por defecto' },
  { k: 'tasa_defecto', label: 'Tasa por defecto' },
  { k: 'ventana_historial_dias', label: 'Días del historial de precios de referencia' },
];

export function CotizadorAjustes({ ajustes, onChange }) {
  const { estado, programar } = useAutoGuardado();
  // Lo que se escribe se guarda tal cual en pantalla (para poder teclear
  // "4," sin que se pierda la coma); a la base solo va lo que ya es número.
  const esValido = (v) => v !== '' && !Number.isNaN(Number(v));
  const guardar = (patch) => {
    onChange({ ...ajustes, ...patch });
    const db = {};
    Object.entries(patch).forEach(([k, v]) => {
      if (k === 'niveles') { if (v.every(esValido)) db[k] = v.map(Number); }
      else if (typeof v === 'boolean' || k === 'hora_vuelo_defecto') db[k] = v;
      else if (esValido(v)) db[k] = Number(v);
    });
    if (Object.keys(db).length) programar('ajustes', db, (p) => guardarAjustes(p));
  };
  const setNivel = (i, v) => {
    const niveles = [...ajustes.niveles];
    niveles[i] = v;
    guardar({ niveles });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p className="section-label" style={{ marginBottom: 0 }}>AJUSTES FIJOS</p>
        <IndicadorGuardado estado={estado} />
      </div>
      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {CAMPOS.map((c) => (
            <Campo key={c.k} label={c.label}>
              <NumInput value={ajustes[c.k]} escala={c.escala || 1} onChange={(v) => guardar({ [c.k]: v })} />
            </Campo>
          ))}
          <Campo label="Hora del vuelo si no se escribe">
            <input type="time" value={ajustes.hora_vuelo_defecto || ''} onChange={(e) => e.target.value && guardar({ hora_vuelo_defecto: e.target.value })} style={inputStyle} />
          </Campo>
        </div>
      </div>

      <p className="section-label">NIVELES DE "HASTA CUÁNTO PAGAR" (R$ de ganancia)</p>
      <div style={{ ...cardStyle, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
        {ajustes.niveles.map((n, i) => (
          <NumInput key={i} value={n} onChange={(v) => setNivel(i, v)} />
        ))}
      </div>

      <p className="section-label">HERRAMIENTAS DEL PANEL</p>
      <label style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!ajustes.mostrar_herramientas_ocultas}
          onChange={(e) => guardar({ mostrar_herramientas_ocultas: e.target.checked })}
          style={{ width: 20, height: 20, padding: 0 }}
        />
        <span style={{ fontSize: 13, color: 'var(--white)', lineHeight: 1.4 }}>
          Mostrar Referidos Vuelos, Anuncios y Generador de Imágenes
          <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)' }}>Apagado = escondidas. Se actualiza al cambiar de pestaña.</span>
        </span>
      </label>
    </div>
  );
}

// ── Horarios del ómnibus ─────────────────────────────────────────────
function HorarioCard({ h, onGuardado }) {
  const [fila, setFila] = useState(h);
  const { estado, programar } = useAutoGuardado();
  const set = (patch) => {
    const nueva = { ...fila, ...patch };
    setFila(nueva);
    if (nueva.sale && nueva.llega) programar(`h:${h.id}`, nueva, (p) => guardarHorario(p));
  };
  const quitar = async () => {
    if (!window.confirm('¿Quitar este horario?')) return;
    try { await borrarHorario(h.id); onGuardado(); } catch { toast('No se pudo quitar.', 'error'); }
  };
  return (
    <div style={{ ...cardStyle, opacity: fila.activo ? 1 : 0.55 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <input value={fila.nombre} onChange={(e) => set({ nombre: e.target.value })} style={{ ...inputStyle, width: '60%', fontWeight: 700 }} />
        <IndicadorGuardado estado={estado} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
        <Campo label="Sale"><input type="time" value={fila.sale || ''} onChange={(e) => set({ sale: e.target.value })} style={inputStyle} /></Campo>
        <Campo label="Llega"><input type="time" value={fila.llega || ''} onChange={(e) => set({ llega: e.target.value })} style={inputStyle} /></Campo>
        <Campo label="+ días">
          <select value={fila.mas_dias} onChange={(e) => set({ mas_dias: Number(e.target.value) })} style={inputStyle}>
            <option value={0}>0 (mismo día)</option>
            <option value={1}>1 (día sig.)</option>
            <option value={2}>2</option>
          </select>
        </Campo>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <Campo label="Precio Total propio"><NumInput value={fila.precio} placeholder="vacío = precio fijo" onChange={(v) => set({ precio: v })} /></Campo>
        <Campo label="Nota"><input value={fila.nota || ''} onChange={(e) => set({ nota: e.target.value })} style={inputStyle} /></Campo>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
        <Campo label="Empresa (Detallado)"><input value={fila.empresa || ''} onChange={(e) => set({ empresa: e.target.value })} placeholder="Amatur" style={inputStyle} /></Campo>
        <Campo label="Terminal salida"><input value={fila.terminal_origen || ''} onChange={(e) => set({ terminal_origen: e.target.value })} placeholder="Rodoviária de Boa Vista" style={inputStyle} /></Campo>
        <Campo label="Terminal llegada"><input value={fila.terminal_destino || ''} onChange={(e) => set({ terminal_destino: e.target.value })} placeholder="Rodoviária de Manaus" style={inputStyle} /></Campo>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0, cursor: 'pointer' }}>
          <input type="checkbox" checked={fila.activo} onChange={(e) => set({ activo: e.target.checked })} style={{ width: 18, height: 18, padding: 0 }} />
          Activo
        </label>
        <button onClick={quitar} style={{ ...btnChico, color: 'var(--error)' }}>Quitar</button>
      </div>
    </div>
  );
}

export function CotizadorHorarios({ horarios, onRecargar }) {
  const agregar = async () => {
    try {
      await guardarHorario({ orden: horarios.length + 1, nombre: 'Nuevo horario', sale: '12:00', llega: '23:00', mas_dias: 0, precio: null, activo: true, nota: '' });
      onRecargar();
    } catch { toast('No se pudo agregar.', 'error'); }
  };
  return (
    <div>
      <p className="section-label">HORARIOS DEL ÓMNIBUS BOA VISTA → MANAUS</p>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.6 }}>
        La app prueba cada horario activo (hostal + ómnibus) y elige el más barato que llega a tiempo. Si hay empate, gana el primero de la lista.
      </p>
      {horarios.map((h) => <HorarioCard key={h.id || h.nombre} h={h} onGuardado={onRecargar} />)}
      <button onClick={agregar} style={{ ...btnChico, width: '100%', color: 'var(--orange)', borderColor: 'var(--orange)', padding: '12px 0', fontSize: 13 }}>
        + Agregar horario
      </button>
    </div>
  );
}

// ── Conversor de dólares ─────────────────────────────────────────────
const LS_TASAS = 'cotizador_tasas_conversor';
const leerTasas = () => {
  try {
    const v = JSON.parse(localStorage.getItem(LS_TASAS));
    if (Array.isArray(v) && v.length === TASAS_CONVERSOR.length) return v;
  } catch {}
  return TASAS_CONVERSOR;
};

export function CotizadorConversor() {
  const [usd, setUsd] = useState('');
  const [brl, setBrl] = useState('');
  const [tasas, setTasas] = useState(leerTasas);
  const cambiarTasa = (i, v) => {
    const nuevas = tasas.map((t, j) => (j === i ? v : t));
    setTasas(nuevas);
    try { localStorage.setItem(LS_TASAS, JSON.stringify(nuevas)); } catch {}
  };
  return (
    <div>
      <p className="section-label">DÓLARES</p>
      <div style={{ ...cardStyle }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <Campo label="US$"><NumInput value={usd} onChange={setUsd} /></Campo>
          <Campo label="R$"><NumInput value={brl} onChange={setBrl} /></Campo>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr 1fr', gap: 6, fontSize: 10, fontWeight: 700, color: 'var(--muted)', paddingBottom: 4 }}>
          <span>TASA</span><span style={{ textAlign: 'right' }}>US$ → R$</span><span style={{ textAlign: 'right' }}>R$ → US$</span>
        </div>
        {tasas.map((t, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr 1fr', gap: 6, alignItems: 'center', padding: '4px 0' }}>
            <NumInput value={t} onChange={(v) => cambiarTasa(i, v)} />
            <span style={{ textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>{esNum(usd) && esNum(t) ? fmtBRL(Number(usd) * Number(t)) : '—'}</span>
            <span style={{ textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>{esNum(brl) && Number(t) > 0 ? fmtBRL(Number(brl) / Number(t)) : '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
